/**
 * Card 1 — template name + the app's existing two access-level radio cards.
 * (Kept as radio cards by explicit product decision — not a dropdown.)
 *
 * A User level template can be shared with chosen people, inside its card:
 * the owner and the people it is shared with as avatars, and a button that
 * adds more (AccessMembersPicker). Left empty, the template is private to its
 * owner as before. The list is kept while Account level is selected and comes
 * back on switching back.
 */
import { RadioButton, TextField } from '@vibe/core';
import React, { useEffect } from 'react';
import AccessMembersPicker from './AccessMembersPicker';

const NAME_INPUT_ID = 'spt-template-name';

export default function TemplateDetailsCard({
  name,
  nameError,
  nameInputRef, // the editor focuses the field when Save is clicked without a name
  savedAs, // the name Save will use when the typed one is taken (lib/templateNames), else null
  accessLevel,
  onNameChange,
  onAccessChange,
  accessMembers = [], // [{ mondayUserId, mondayUserName }] the template is shared with (lib/accessMembers)
  onAccessMembersChange, // (members) — given, the User level card shows who it is shared with
  ownerId, // the template's owner; the person editing, on a new template
  viewerId, // the person editing
  people, // useAccountUsers: { status, users, error, load }
}) {
  const sharing = Boolean(onAccessMembersChange);
  // the account's people are read when the share row first shows
  const loadPeople = people && people.load;
  useEffect(() => {
    if (sharing && accessLevel === 'user_level' && loadPeople) loadPeople();
  }, [sharing, accessLevel, loadPeople]);
  return (
    <div className="mb-6 bg-surface border border-subtle rounded-lg shadow-sm">
      <div className="p-6 space-y-6">
        <div className="max-w-md" data-tour="name">
          {/*
            Own label rather than TextField's `title`: Vibe's `required` only sets the
            attribute on the input and draws no asterisk.
          */}
          <label htmlFor={NAME_INPUT_ID} className="mb-2 spt-label">
            Template name
            <span className="ml-0.5 text-danger" aria-hidden="true">
              *
            </span>
          </label>
          <TextField
            id={NAME_INPUT_ID}
            placeholder="e.g. Production readiness checklist"
            type="text"
            required
            value={name}
            onChange={onNameChange}
            setRef={(node) => {
              if (nameInputRef) nameInputRef.current = node;
            }}
            validation={nameError ? { status: 'error', text: 'This field is required' } : {}}
          />
          {savedAs && !nameError && (
            <p className="mt-1.5 text-xs text-secondary" role="status">
              A template named “{name.trim()}” already exists, so this one will be saved as{' '}
              <span className="font-medium text-primary">“{savedAs}”</span>.
            </p>
          )}
        </div>

        <div data-tour="access">
          <span className="mb-2 spt-label" id="spt-access-label">Access level</span>
          <div className="space-y-3" role="radiogroup" aria-labelledby="spt-access-label">
            <div className="p-4 border border-subtle rounded-lg">
              <div className="flex items-center">
                <RadioButton
                  text="User level"
                  name="spt-editor-access"
                  checked={accessLevel === 'user_level'}
                  onSelect={() => onAccessChange('user_level')}
                  value="user_level"
                />
                <div className="ml-3">
                  <p className="text-sm text-secondary">{sharing ? 'Only you, and anyone you share it with, can access this template' : 'Only you can access this template'}</p>
                </div>
              </div>
              {sharing && accessLevel === 'user_level' && (
                <AccessMembersPicker members={accessMembers} ownerId={ownerId} viewerId={viewerId} people={people} onChange={onAccessMembersChange} />
              )}
            </div>
            <div className="flex items-center p-4 border border-subtle rounded-lg">
              <RadioButton
                text="Account level"
                name="spt-editor-access"
                checked={accessLevel === 'account_level'}
                onSelect={() => onAccessChange('account_level')}
                value="account_level"
              />
              <div className="ml-3">
                <p className="text-sm text-secondary">Everyone in your account can access this template</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
